import type { Cents } from "./money";

/**
 * Acquisition Cost Line — an itemized cost of acquiring the property,
 * tracked separately from renovation cost (master spec Section 15).
 *
 * Structured acquisition lines are the primary Phase 2 mechanism for
 * acquisition expenses; the general Actual Expense workflow must not
 * duplicate these amounts (no double-counting).
 *
 * Earnest money: recorded here so the historical cash event is preserved,
 * with `creditedTowardPurchase = true` by default — a credited deposit is
 * already inside Purchase Price, so it contributes $0 of additional
 * acquisition expense. If a deposit becomes a true cost (e.g. forfeited on
 * a dead deal), the flag is turned off and the engine then includes it.
 */
export type AcquisitionKind =
  | "earnest-money"
  | "inspection"
  | "appraisal"
  | "title-fees"
  | "lender-fees"
  | "closing-costs"
  | "initial-repairs"
  | "other";

export const ACQUISITION_KINDS: ReadonlyArray<{ id: AcquisitionKind; displayName: string }> = [
  { id: "earnest-money", displayName: "Earnest Money" },
  { id: "inspection", displayName: "Inspection" },
  { id: "appraisal", displayName: "Appraisal" },
  { id: "title-fees", displayName: "Title Fees" },
  { id: "lender-fees", displayName: "Lender Fees" },
  { id: "closing-costs", displayName: "Closing Costs (buy side)" },
  { id: "initial-repairs", displayName: "Initial Repairs" },
  { id: "other", displayName: "Other" },
];

export function acquisitionKindDisplayName(id: AcquisitionKind): string {
  return ACQUISITION_KINDS.find((k) => k.id === id)?.displayName ?? id;
}

export interface AcquisitionCostLine {
  id: string;
  projectId: string;
  kind: AcquisitionKind;
  description: string;
  amountCents: Cents;
  creditedTowardPurchase: boolean;
  /** Optional payment date (ISO date, e.g. "2026-08-24"). */
  date: string | null;
  notes: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AcquisitionCostLineInput {
  kind: AcquisitionKind;
  description: string;
  amountCents: Cents;
  creditedTowardPurchase: boolean;
  date: string | null;
  notes: string;
}

export function validateAcquisitionCostLineInput(input: AcquisitionCostLineInput): string[] {
  const problems: string[] = [];
  if (!Number.isSafeInteger(input.amountCents)) {
    problems.push("Amount must be a valid dollar amount.");
  }
  return problems;
}
